import {
  ApplicationConfig,
  DOCUMENT,
  Injector,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  CsvExportModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  ColumnApiModule,
  DateFilterModule,
  RowStyleModule,
  SelectEditorModule,
  TextEditorModule,
  TextFilterModule,
} from 'ag-grid-community';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { provideIcons, provideNgIconsConfig } from '@ng-icons/core';
import {
  tablerAlertTriangle,
  tablerArrowLeft,
  tablerArrowRight,
  tablerBell,
  tablerBellOff,
  tablerBellRinging,
  tablerBroadcast,
  tablerCar,
  tablerCheck,
  tablerChevronDown,
  tablerChevronLeft,
  tablerChevronRight,
  tablerChevronsLeft,
  tablerChevronsRight,
  tablerClock,
  tablerCoin,
  tablerCopy,
  tablerCurrentLocation,
  tablerDeviceFloppy,
  tablerEdit,
  tablerExternalLink,
  tablerEye,
  tablerEyeOff,
  tablerFileUpload,
  tablerFlag,
  tablerHistory,
  tablerInfoCircle,
  tablerLink,
  tablerLinkOff,
  tablerList,
  tablerListCheck,
  tablerLoader2,
  tablerLock,
  tablerLockOpen,
  tablerLogout,
  tablerMail,
  tablerMailForward,
  tablerPencil,
  tablerPhone,
  tablerPlayerStop,
  tablerPlus,
  tablerProgressCheck,
  tablerRefresh,
  tablerSearch,
  tablerSettings,
  tablerShare,
  tablerShieldCheck,
  tablerShieldExclamation,
  tablerTransfer,
  tablerUser,
  tablerUsers,
  tablerViewfinder,
  tablerVolume,
  tablerWorld,
  tablerX,
} from '@ng-icons/tabler-icons';

import { routes } from './app.routes';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { DEFAULT_LANG } from './shared/utils/supported-langs';
import { LanguageService } from './core/services/language.service';
import { SiteService } from './core/services/site.service';
import { BRAND, applyFavicon } from './core/config/brand';
/** Every tabler icon used across the app — also reused by component specs so rendered
 * `<ng-icon>`s resolve in tests instead of warning "No icon named X was found". */
export const APP_ICONS = {
  tablerAlertTriangle,
  tablerArrowLeft,
  tablerArrowRight,
  tablerBell,
  tablerBellOff,
  tablerBellRinging,
  tablerBroadcast,
  tablerCar,
  tablerCheck,
  tablerChevronDown,
  tablerChevronLeft,
  tablerChevronRight,
  tablerChevronsLeft,
  tablerChevronsRight,
  tablerClock,
  tablerCoin,
  tablerCopy,
  tablerCurrentLocation,
  tablerDeviceFloppy,
  tablerEdit,
  tablerExternalLink,
  tablerEye,
  tablerEyeOff,
  tablerFileUpload,
  tablerFlag,
  tablerHistory,
  tablerInfoCircle,
  tablerLink,
  tablerLinkOff,
  tablerList,
  tablerListCheck,
  tablerLoader2,
  tablerLock,
  tablerLockOpen,
  tablerLogout,
  tablerMail,
  tablerMailForward,
  tablerPencil,
  tablerPhone,
  tablerPlayerStop,
  tablerPlus,
  tablerProgressCheck,
  tablerRefresh,
  tablerSearch,
  tablerSettings,
  tablerShare,
  tablerShieldCheck,
  tablerShieldExclamation,
  tablerTransfer,
  tablerUser,
  tablerUsers,
  tablerViewfinder,
  tablerVolume,
  tablerWorld,
  tablerX,
};

// AllCommunityModule alone pushed the initial bundle past the 1MB build budget;
// register only what the grid pages actually need.
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnApiModule,
  RowSelectionModule,
  RowStyleModule,
  ColumnAutoSizeModule,
  CellStyleModule,
  CsvExportModule,
  TextEditorModule,
  SelectEditorModule,
  TextFilterModule,
  DateFilterModule,
  NumberFilterModule,
  PaginationModule,
  // Grid API calls fail at runtime (AG Grid error #200) unless their module is registered:
  // RenderApiModule → refreshCells, RowApiModule → forEachNode.
  RenderApiModule,
  RowApiModule,
]);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideNgIconsConfig({ size: '1em' }),
    provideIcons(APP_ICONS),
    provideTranslateService({
      fallbackLang: DEFAULT_LANG,
      lang: DEFAULT_LANG,
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json',
      }),
    }),
    provideAppInitializer(() => {
      // Instantiating resolves+applies the saved/browser/default lang before first render.
      inject(LanguageService);
    }),
    // LJISE-896: which subsidiary's site this is (branding, features) — before first render, so the
    // right logo and tab icon show from the start. Never fails startup: falls back to the build's
    // default brand.
    provideAppInitializer(() => {
      const document = inject(DOCUMENT);
      const injector = inject(Injector);
      return inject(SiteService)
        .load()
        .then(() => applyFavicon(document, injector.get(BRAND)));
    }),
  ],
};
